import { useState, useEffect, useCallback } from 'react';
import { X, Gift, Users, TrendingUp, MessageCircle, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FREE_PICKS_URL = "https://www.winible.com/checkout/1547324105701741163?pid=1547324105714324076";

const affiliateLinks = [
  { name: 'PrizePicks', url: 'https://prizepicks.onelink.me/FjtC/d9ee5h8t', color: 'bg-green-500 hover:bg-green-600' },
  { name: 'Betr', url: 'https://engagebetr.onelink.me/auSX/STATLAB', color: 'bg-purple-500 hover:bg-purple-600' },
  { name: 'No Vig', url: 'https://novig.onelink.me/JHQQ/5wk3auau', color: 'bg-blue-500 hover:bg-blue-600' }
];

export const EmailCapture = () => {
  const [stage, setStage] = useState(0); // 0 = hidden, 1 = first offer, 2 = second offer
  const [hasTriggered, setHasTriggered] = useState(false);
  const [canTrigger, setCanTrigger] = useState(false);

  // Wait 20 seconds before exit intent can trigger
  useEffect(() => {
    const hasSeenExit = localStorage.getItem('psl_exit_seen');
    if (hasSeenExit) return;

    const timer = setTimeout(() => {
      setCanTrigger(true);
    }, 20000); // 20 seconds minimum on page

    return () => clearTimeout(timer);
  }, []);

  // Browser "Leave site?" dialog when they actually try to close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only show if they haven't completed signup
      const hasSeenExit = localStorage.getItem('psl_exit_seen');
      if (!hasSeenExit) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return ''; // Required for some browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Exit intent detection - only when mouse REALLY leaves (way above viewport)
  const handleMouseLeave = useCallback((e) => {
    // Only trigger if:
    // 1. User has been on page 20+ seconds
    // 2. Mouse is WAY above viewport (clientY < -50, not just 0)
    // 3. Hasn't triggered before
    if (canTrigger && e.clientY < -50 && !hasTriggered) {
      const hasSeenExit = localStorage.getItem('psl_exit_seen');
      if (!hasSeenExit) {
        setStage(1);
        setHasTriggered(true);
      }
    }
  }, [hasTriggered, canTrigger]);

  useEffect(() => {
    // Add exit intent listener
    document.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave]);

  const handleClose = () => {
    if (stage === 1) {
      // Show second offer before letting them go
      setStage(2);
    } else {
      // Final close - mark as seen and let them go
      setStage(0);
      localStorage.setItem('psl_exit_seen', 'true');
    }
  };

  const handleAppClick = (url) => {
    window.open(url, '_blank');
  };

  const handleFreePicks = () => {
    window.open(FREE_PICKS_URL, '_blank');
    setStage(0);
    localStorage.setItem('psl_exit_seen', 'true');
  };

  if (stage === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn" data-testid="exit-popup">
      <div className="relative bg-zinc-900 border-2 border-primary w-full max-w-lg p-8 shadow-2xl shadow-primary/30 rounded-lg">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          data-testid="close-exit-popup"
        >
          <X size={24} />
        </button>

        {stage === 1 && (
          <>
            {/* Stage 1: 3 FREE months offer */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="text-yellow-400" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-yellow-400 mb-2">
                WAIT! Before You Go...
              </h2>
              <p className="text-xl text-white mb-2">
                Get <span className="text-yellow-400 font-bold">3 FREE MONTHS</span> of Premium Picks!
              </p>
              <p className="text-gray-400">
                Just sign up for these apps (takes 2 minutes)
              </p>
            </div>

            {/* App buttons */}
            <div className="space-y-3 mb-6">
              {affiliateLinks.map((app) => (
                <button
                  key={app.name}
                  onClick={() => handleAppClick(app.url)}
                  className={`w-full ${app.color} text-white font-bold py-4 rounded-lg transition-all hover:scale-[1.02] flex items-center justify-center gap-2`}
                >
                  Sign Up for {app.name}
                </button>
              ))}
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-500 mb-3">
                After signing up, text screenshots to <span className="text-primary">(346) 326-4149</span>
              </p>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-300 text-sm underline"
              >
                No thanks, I'll pass on free picks
              </button>
            </div>
          </>
        )}

        {stage === 2 && (
          <>
            {/* Stage 2: Lifetime membership offer */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-primary" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">
                Last Chance!
              </h2>
              <p className="text-xl text-primary mb-4">
                Lifetime FREE Membership
              </p>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <TrendingUp className="text-green-400 mx-auto mb-2" size={24} />
                <p className="text-sm text-white font-bold">Free Analysis</p>
                <p className="text-xs text-gray-400">Daily player breakdowns</p>
              </div>
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <MessageCircle className="text-blue-400 mx-auto mb-2" size={24} />
                <p className="text-sm text-white font-bold">Discord Community</p>
                <p className="text-xs text-gray-400">Talk picks with 500+ members</p>
              </div>
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <Gift className="text-yellow-400 mx-auto mb-2" size={24} />
                <p className="text-sm text-white font-bold">Bonuses & Prizes</p>
                <p className="text-xs text-gray-400">Weekly giveaways</p>
              </div>
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <DollarSign className="text-green-400 mx-auto mb-2" size={24} />
                <p className="text-sm text-white font-bold">Free Play Money</p>
                <p className="text-xs text-gray-400">Bankroll to start betting</p>
              </div>
            </div>

            <button
              onClick={handleFreePicks}
              className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-lg transition-all hover:scale-[1.02] mb-4"
            >
              Yes! Give Me Lifetime Access
            </button>

            <button
              onClick={handleClose}
              className="w-full text-gray-500 hover:text-gray-300 text-sm"
            >
              No thanks, I really want to leave
            </button>
          </>
        )}
      </div>
    </div>
  );
};
